/**
 * Custom hook for calculating calendar days with their types (PTO, holiday, non-working, normal)
 *
 * This hook handles:
 * - Extending the date range to include full weeks
 * - Determining day types based on PTO range, holidays, weekends, and work week configuration
 * - Converting dates to a format suitable for the MiniCalendar component
 */

import { useMemo } from 'react'
import { startOfDay, eachDayOfInterval, startOfWeek, endOfWeek, getDay } from 'date-fns'
import { useIntl } from 'react-intl'
import { PTOSuggestion } from '../../../types'
import { toISODateString, normalizeISODateString, createDateRangeSet } from '../utils/dateUtils'
import { getDayType, isWorkingDay } from '../utils/calendarUtils'
import { MiniCalendarDay } from '../mini-calendar/types'

interface UseCalendarDaysParams {
  suggestion: PTOSuggestion
  weekStartsOn: 0 | 1
  workWeekDays?: number[]
  nonWorkingDays?: string[]
}

/**
 * Hook that calculates calendar days for a PTO suggestion
 *
 * @param suggestion - The PTO suggestion containing date range, holidays, and weekends
 * @param weekStartsOn - Week start preference (0 = Sunday, 1 = Monday)
 * @param workWeekDays - Optional array of working day numbers
 * @returns Array of MiniCalendarDay objects ready for MiniCalendar component
 *
 * @example
 * const calendarDays = useCalendarDays({
 *   suggestion: { startDate: '2026-01-01', endDate: '2026-01-05', ... },
 *   weekStartsOn: 1,
 *   workWeekDays: [1, 2, 3, 4, 5]
 * })
 */
export function useCalendarDays({ suggestion, weekStartsOn, workWeekDays, nonWorkingDays }: UseCalendarDaysParams): MiniCalendarDay[] {
  const { formatMessage } = useIntl()

  return useMemo(() => {
    // Parse and normalize start and end dates
    const startDateStr = normalizeISODateString(suggestion.startDate)
    const endDateStr = normalizeISODateString(suggestion.endDate)

    // Parse the date strings to get year, month, day for generating the range
    // These are calendar dates from the API (YYYY-MM-DD), not timezone-dependent
    const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number)
    const [endYear, endMonth, endDay] = endDateStr.split('-').map(Number)

    // Create local dates for the start and end (at midnight local time)
    // This ensures the calendar displays the correct dates regardless of timezone
    const startLocal = new Date(startYear, startMonth - 1, startDay)
    const endLocal = new Date(endYear, endMonth - 1, endDay)

    // Extend range to include weekends before/after for better visualization
    const startLocalNormalized = startOfDay(startLocal)
    const endLocalNormalized = startOfDay(endLocal)
    const weekStart = startOfWeek(startLocalNormalized, { weekStartsOn })
    const weekEnd = endOfWeek(endLocalNormalized, { weekStartsOn })

    const allDays = eachDayOfInterval({ start: weekStart, end: weekEnd })

    // Create PTO date set - includes ALL days from start to end (inclusive)
    const ptoDateSet = createDateRangeSet(startLocal, endLocal)

    // Create maps for quick lookup - normalize ISO date strings to YYYY-MM-DD for comparison
    const holidayMap = new Map(
      (suggestion.holidaysIncluded || []).map((h) => {
        const normalizedDate = normalizeISODateString(h.date)
        return [normalizedDate, h.name]
      })
    )
    const weekendDateSet =
      nonWorkingDays && nonWorkingDays.length > 0
        ? new Set(nonWorkingDays.map((date) => normalizeISODateString(date)))
        : new Set((suggestion.weekendsIncluded || []).map((w) => normalizeISODateString(w.date)))
    const holidayDateSet = new Set(holidayMap.keys())

    // Convert calendar days to MiniCalendarDay format
    const miniCalendarDays: MiniCalendarDay[] = allDays.map((date) => {
      // Normalize date to local date string for comparison with API dates
      const dateStr = toISODateString(date, false)
      const normalizedDate = startOfDay(date) // Keep for display purposes
      const dayOfWeek = getDay(normalizedDate)

      // Determine day type using utility function
      const dayInfo = getDayType(dateStr, normalizedDate, ptoDateSet, holidayDateSet, weekendDateSet, workWeekDays, holidayMap, formatMessage)

      // Convert internal day type to MiniCalendar day type
      let type: 'pto' | 'weekend' | 'holiday' | 'workday' | 'empty' = 'empty'

      if (dayInfo.type === 'pto') {
        type = 'pto'
      } else if (dayInfo.type === 'holiday') {
        type = 'holiday'
      } else if (dayInfo.type === 'non-working') {
        type = 'weekend'
      } else if (dayInfo.type === 'normal') {
        // Check if this is a working day based on workWeekDays
        const isWorking = isWorkingDay(dayOfWeek, workWeekDays)

        if (isWorking) {
          type = 'workday' // Normal working days outside PTO range (shown as white columns)
        } else {
          type = 'empty' // Non-working days outside PTO range
        }
      }

      return {
        date: normalizedDate,
        type,
        label: dayInfo.label,
      }
    })

    return miniCalendarDays
  }, [suggestion, weekStartsOn, workWeekDays, nonWorkingDays, formatMessage])
}
